import {
  GetWasteShipmentQuery,
  UnitType,
  WasteClassification,
  WasteShipmentLineOutcome,
} from "generated-graphql/graphql";
import { useCallback, useMemo, useState } from "react";

type Shipment = NonNullable<GetWasteShipmentQuery["wasteShipment"]>;
type ShipmentLine = Shipment["lines"][number];

/**
 * Only the parts of a shipment the staging logic reads. Declared structurally
 * rather than taking the whole query type so the hook can be exercised from a
 * test without standing up a full `GetWasteShipmentQuery` result — the real
 * caller's `Shipment` satisfies it as-is.
 */
export type StageableShipment = {
  id: string;
  lines: ShipmentLine[];
};

/**
 * What staging needs from a container picked in the builder — the fields
 * `toDisplayContainer` reads plus the ones the pending line snapshots. Narrower
 * than `ShippableContainerItemFragment` on purpose: the hook has no use for the
 * rest of that fragment, and demanding it makes the logic untestable without a
 * full GraphQL result.
 */
export type StageableContainer = Parameters<typeof toDisplayContainer>[0] & {
  capacityUnit?: UnitType | null;
  wasteStream?: WasteStreamIdentity | null;
};

/**
 * The stream as a line records it. Classification rides along because the
 * contents grid puts it on the stream's group row, the same as both container
 * pickers do on their stream headings.
 */
type WasteStreamIdentity = {
  id: string;
  name: string;
  classification?: WasteClassification | null;
};

/**
 * A row in the contents card: either a saved line, or one the user has staged
 * to add or remove but has not saved yet.
 */
export type DisplayLine = {
  /** Stable key. Saved lines use their line id; pending adds use the container's. */
  key: string;
  /** Null until the line exists on the server. */
  lineId: string | null;
  pending: "added" | "removed" | null;
  quantity: number;
  unit: UnitType;
  federalWasteCodes: string[];
  stateWasteCodes: string[];
  dotDescription: string | null;
  profileNumber: string | null;
  wasteStream: WasteStreamIdentity | null;
  /**
   * What shipping did to the container, as the line recorded it. Null on a
   * staged row — it has never shipped — and on lines that shipped before the
   * outcome was recorded, which read it back off the container instead.
   */
  shipOutcome: WasteShipmentLineOutcome | null;
  /**
   * Only the container facts the shipment views read. Declared structurally
   * rather than as `ShipmentLine["container"]` because a staged row is built
   * from the builder's shippable fragment, which carries no `archivedAt` —
   * casting the two together would let a view read fields a pending row has
   * never had.
   */
  container: {
    id: string;
    containerLabel: string;
    containerType: string;
    amount: number | null;
    amountUnit: UnitType | null;
    isPermanent: boolean;
    archivedAt: string | null;
    accumulationArea: { name: string } | null;
    /**
     * When the container's accumulation clock runs out, or null when the engine
     * could not compute one. Read by the contents grid while the shipment is
     * still on site.
     */
    deadlineDate: string | null;
  };
};

/**
 * Narrow either container shape — a saved line's or the builder's shippable
 * fragment — onto the fields the shipment views read, normalizing the
 * optionality the two disagree on.
 */
function toDisplayContainer(
  container: {
    id: string;
    containerLabel: string;
    containerType: string;
    amount?: number | null;
    amountUnit?: UnitType | null;
    isPermanent: boolean;
    archivedAt?: string | null;
    accumulationArea?: { name: string } | null;
    clock?: { effectiveDeadlineDate?: string | null } | null;
  },
  archivedAtOverride?: string | null
): DisplayLine["container"] {
  return {
    id: container.id,
    containerLabel: container.containerLabel,
    containerType: container.containerType,
    amount: container.amount ?? null,
    amountUnit: container.amountUnit ?? null,
    isPermanent: container.isPermanent,
    archivedAt:
      archivedAtOverride !== undefined ? archivedAtOverride : (container.archivedAt ?? null),
    accumulationArea: container.accumulationArea ?? null,
    deadlineDate: container.clock?.effectiveDeadlineDate ?? null,
  };
}

/**
 * Container edits on a shipment are staged, not written as you click, so the
 * page has one save. Removing a container and changing your mind should cost
 * nothing, and a half-finished set of changes should never be what the vendor
 * sees.
 *
 * The batch commits through a single `updateShipmentLines` call — the mutation
 * already takes adds, removals, and quantities together — so a save is still
 * one atomic server operation.
 */
export function useStagedShipmentLines(shipment: StageableShipment) {
  const [addedContainers, setAddedContainers] = useState<StageableContainer[]>([]);
  const [removedLineIds, setRemovedLineIds] = useState<string[]>([]);
  const [quantityEdits, setQuantityEdits] = useState<
    Array<{ lineId: string; quantity: number; unit: UnitType }>
  >([]);

  const reset = useCallback(() => {
    setAddedContainers([]);
    setRemovedLineIds([]);
    setQuantityEdits([]);
  }, []);

  const addContainers = useCallback((containers: StageableContainer[]) => {
    setAddedContainers((current) => {
      const known = new Set(current.map((c) => c.id));
      return [...current, ...containers.filter((c) => !known.has(c.id))];
    });
  }, []);

  const removeLine = useCallback((line: DisplayLine) => {
    // A staged addition is dropped outright; a saved line is marked for
    // removal so Discard can bring it back.
    if (line.lineId === null) {
      setAddedContainers((current) => current.filter((c) => c.id !== line.container.id));
      return;
    }
    const lineId = line.lineId;
    setRemovedLineIds((current) => (current.includes(lineId) ? current : [...current, lineId]));
    setQuantityEdits((current) => current.filter((q) => q.lineId !== lineId));
  }, []);

  const restoreLine = useCallback((line: DisplayLine) => {
    setRemovedLineIds((current) => current.filter((id) => id !== line.lineId));
  }, []);

  const setQuantity = useCallback((lineId: string, quantity: number, unit: UnitType) => {
    setQuantityEdits((current) => [
      ...current.filter((q) => q.lineId !== lineId),
      { lineId, quantity, unit },
    ]);
  }, []);

  /*
    One memo over the whole derivation, because its identity is what the contents
    grid is handed as `rows`. Rebuilt every render, it gave MUI a new row array on
    every keystroke in the Details form — the page re-renders on each one, since
    the dirty-field count feeds the header — and took the grid's own memos over
    rows, rollups and columns down with it, since all three key off this array.
  */
  const displayLines = useMemo<DisplayLine[]>(() => {
    const editFor = (lineId: string) => quantityEdits.find((q) => q.lineId === lineId);

    const savedLines: DisplayLine[] = shipment.lines.map((line) => {
      const edit = editFor(line.id);
      return {
        key: line.id,
        lineId: line.id,
        pending: removedLineIds.includes(line.id) ? "removed" : null,
        quantity: edit?.quantity ?? line.quantity,
        unit: edit?.unit ?? line.unit,
        federalWasteCodes: line.federalWasteCodes,
        stateWasteCodes: line.stateWasteCodes,
        dotDescription: line.dotDescription ?? null,
        profileNumber: line.profileNumber ?? null,
        wasteStream: line.wasteStream ?? null,
        shipOutcome: line.shipOutcome ?? null,
        container: toDisplayContainer(line.container),
      };
    });

    const pendingLines: DisplayLine[] = addedContainers.map((container) => ({
      key: `pending-${container.id}`,
      lineId: null,
      pending: "added",
      // A staged container ships its full amount; a partial needs a saved line
      // to edit against, so the quantity control stays disabled until then.
      quantity: container.amount ?? 0,
      unit: container.amountUnit ?? container.capacityUnit ?? UnitType.Gallons,
      federalWasteCodes: [],
      stateWasteCodes: [],
      dotDescription: null,
      profileNumber: null,
      wasteStream: container.wasteStream ?? null,
      shipOutcome: null,
      // A container only reaches the builder's pool while it is active, so a
      // staged row is never archived — stated rather than assumed.
      container: toDisplayContainer(container, null),
    }));

    return [...savedLines, ...pendingLines];
  }, [shipment.lines, addedContainers, removedLineIds, quantityEdits]);

  const changeCount = addedContainers.length + removedLineIds.length + quantityEdits.length;

  /** The staged batch as one mutation input, or null when nothing is staged. */
  const toUpdateLinesInput = () =>
    changeCount === 0
      ? null
      : {
          shipmentId: shipment.id,
          addContainerIds: addedContainers.map((c) => c.id),
          removeLineIds: removedLineIds,
          setQuantities: quantityEdits,
        };

  /**
   * Lines that will exist after saving — what validation should judge.
   *
   * Memoized for the same reason as `displayLines` above: the add-containers
   * dialog turns this into a Set and filters and groups the facility's whole
   * shippable-container list against it, all keyed on this array's identity.
   */
  const stagedContainerIds = useMemo(
    () => displayLines.filter((l) => l.pending !== "removed").map((l) => l.container.id),
    [displayLines]
  );

  return {
    displayLines,
    stagedContainerIds,
    changeCount,
    hasChanges: changeCount > 0,
    addContainers,
    removeLine,
    restoreLine,
    setQuantity,
    reset,
    toUpdateLinesInput,
  };
}
